import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Send,
  Cancel,
  CheckCircle,
  HighlightOff,
  Payments,
  AccountBalance,
} from '@mui/icons-material';
import { RouterLink } from '../../utils/linkNav';
import { PageHeader, StatusDot, StatusTone } from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import { ExpenseStatus, PaidBy, PaymentMethod, STATUS_TONE, fmtDate, toNum } from './ExpensePage';
import AttachmentsPanel from '../../components/common/AttachmentsPanel';

// ---------------------------------------------------------------------------
// Types -- raw snake_case shape expected from GET /expenses/:id. Joined
// display names (employee_name, cost_center_name, ...) are optional -- the
// page degrades to showing the raw id when a backend join isn't present.
// ---------------------------------------------------------------------------

interface ExpenseDetail {
  id: number;
  expense_number: string;
  expense_date: string;
  employee_id: number | string | null;
  employee_name?: string | null;
  category: string;
  description: string | null;
  amount: number | string;
  tax_amount: number | string;
  total_amount: number | string;
  cost_center_id: number | string | null;
  cost_center_name?: string | null;
  cost_center_code?: string | null;
  project_id?: number | string | null;
  project_name?: string | null;
  department: string | null;
  payment_method: PaymentMethod | null;
  paid_by: PaidBy;
  vendor_id?: string | null;
  vendor_name?: string | null;
  gl_account_code: string | null;
  remarks: string | null;
  status: ExpenseStatus;
  approved_by_name?: string | null;
  approved_date?: string | null;
  rejection_reason?: string | null;
  paid_date?: string | null;
  payment_reference?: string | null;
  created_at?: string;
}

const FLOW_STEPS: { key: string; label: string }[] = [
  { key: 'DRAFT', label: 'Draft' },
  { key: 'SUBMITTED', label: 'Submitted' },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'PAID', label: 'Paid' },
];

const FLOW_INDEX: Partial<Record<ExpenseStatus, number>> = {
  DRAFT: 0,
  SUBMITTED: 1,
  APPROVED: 2,
  PAID: 3,
};

const todayISO = () => new Date().toISOString().slice(0, 10);

/** GL-impact narrative for the approval posting, derived from the current
 * expense so the note stays accurate as amount/tax/payment method change. */
const glImpactLines = (exp: ExpenseDetail): string[] => {
  const lines: string[] = [];
  const acct = exp.gl_account_code ? `${exp.category} (${exp.gl_account_code})` : `${exp.category} Expense A/c`;
  lines.push(`Dr ${acct} — ${formatIndianNumber(toNum(exp.amount))}`);
  if (toNum(exp.tax_amount) > 0) {
    lines.push(`Dr Input GST A/c — ${formatIndianNumber(toNum(exp.tax_amount))}`);
  }
  if (exp.paid_by === 'EMPLOYEE_REIMBURSEMENT') {
    lines.push(`Cr Accounts Payable (Employee Reimbursement) — ${formatIndianNumber(toNum(exp.total_amount))}`);
  } else {
    const isCash = exp.payment_method === 'CASH' || exp.payment_method === 'PETTY_CASH';
    lines.push(`Cr ${isCash ? 'Cash' : 'Bank'} A/c — ${formatIndianNumber(toNum(exp.total_amount))}`);
  }
  return lines;
};

const ExpenseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [exp, setExp] = useState<ExpenseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Dialog state
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveComment, setApproveComment] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState(todayISO());
  const [paymentReference, setPaymentReference] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { expense } = await api.get<{ expense: ExpenseDetail }>(`/expenses/${id}`);
      setExp(expense);
    } catch (err: any) {
      setError(err.message || 'Failed to load expense');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !exp) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/finance/expenses')} sx={{ mb: 2 }}>
          Back to Expenses
        </Button>
        <Alert severity="error">{error || 'Expense not found'}</Alert>
      </Box>
    );
  }

  const isSideState = (['REJECTED', 'CANCELLED'] as ExpenseStatus[]).includes(exp.status);
  const flowIndex = FLOW_INDEX[exp.status] ?? 0;

  // Action visibility keyed to status, per the workflow:
  //   DRAFT      -> Edit, Submit, Cancel
  //   SUBMITTED  -> Approve, Reject
  //   APPROVED   -> Mark Paid, Cancel
  const canEdit = exp.status === 'DRAFT';
  const canSubmit = exp.status === 'DRAFT';
  const canCancel = exp.status === 'DRAFT' || exp.status === 'APPROVED';
  const canApproveReject = exp.status === 'SUBMITTED';
  const canMarkPaid = exp.status === 'APPROVED';

  const runAction = async (name: string, fn: () => Promise<void>) => {
    setActionLoading(name);
    setActionError(null);
    try {
      await fn();
    } catch (err: any) {
      setActionError(err.message || `Failed to ${name}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubmit = () =>
    runAction('submit', async () => {
      await api.post(`/expenses/${id}/submit`);
      setNotice('Expense submitted for approval.');
      await load();
    });

  const handleApprove = () =>
    runAction('approve', async () => {
      await api.post(`/expenses/${id}/approve`, { comment: approveComment.trim() || undefined });
      setApproveOpen(false);
      setApproveComment('');
      setNotice('Expense approved.');
      await load();
    });

  const handleReject = () =>
    runAction('reject', async () => {
      await api.post(`/expenses/${id}/reject`, { reason: rejectReason.trim() });
      setRejectOpen(false);
      setRejectReason('');
      setNotice('Expense rejected.');
      await load();
    });

  const handleCancel = () =>
    runAction('cancel', async () => {
      await api.post(`/expenses/${id}/cancel`);
      setCancelOpen(false);
      setNotice('Expense cancelled.');
      await load();
    });

  const handleMarkPaid = () =>
    runAction('mark-paid', async () => {
      await api.post(`/expenses/${id}/mark-paid`, {
        paymentDate: paymentDate || undefined,
        paymentReference: paymentReference.trim() || undefined,
      });
      setMarkPaidOpen(false);
      setPaymentReference('');
      setNotice('Expense marked as paid.');
      await load();
    });

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/finance/expenses')} sx={{ mb: 1 }}>
        Back to Expenses
      </Button>

      <PageHeader
        title={exp.expense_number}
        qualifier={exp.department ? `· ${exp.department}` : undefined}
        subtitle={`Recorded ${fmtDate(exp.expense_date)}${exp.employee_name ? ` for ${exp.employee_name}` : ''}`}
        actions={
          <>
            <StatusDot label={exp.status.replace(/_/g, ' ')} tone={STATUS_TONE[exp.status]} />
            <Chip
              size="small"
              label={exp.paid_by === 'EMPLOYEE_REIMBURSEMENT' ? 'Reimbursement' : 'Company Paid'}
              color={exp.paid_by === 'EMPLOYEE_REIMBURSEMENT' ? 'info' : 'default'}
            />
          </>
        }
      />

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Lifecycle ribbon */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        {isSideState ? (
          <>
            <Stepper activeStep={-1} alternativeLabel sx={{ opacity: 0.45, mb: 2 }}>
              {FLOW_STEPS.map((s) => (
                <Step key={s.key}>
                  <StepLabel>{s.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Alert severity={exp.status === 'CANCELLED' ? 'warning' : 'error'} icon={<Cancel />}>
              <Typography sx={{ fontWeight: 700 }}>Expense {exp.status.replace(/_/g, ' ')}</Typography>
              {exp.status === 'REJECTED' && exp.rejection_reason && (
                <Typography variant="body2">{exp.rejection_reason}</Typography>
              )}
            </Alert>
          </>
        ) : (
          <Stepper activeStep={flowIndex} alternativeLabel>
            {FLOW_STEPS.map((s, i) => (
              <Step key={s.key} completed={i < flowIndex}>
                <StepLabel>{s.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Paper>

      {/* Workflow actions */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Workflow Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {canEdit && (
            <Button variant="outlined" startIcon={<Edit />} component={RouterLink} to={`/finance/expenses/${id}/edit`}>
              Edit
            </Button>
          )}
          {canSubmit && (
            <Button variant="contained" startIcon={<Send />} disabled={actionLoading !== null} onClick={handleSubmit}>
              {actionLoading === 'submit' ? 'Submitting...' : 'Submit for Approval'}
            </Button>
          )}
          {canApproveReject && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                disabled={actionLoading !== null}
                onClick={() => setApproveOpen(true)}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<HighlightOff />}
                disabled={actionLoading !== null}
                onClick={() => setRejectOpen(true)}
              >
                Reject
              </Button>
            </>
          )}
          {canMarkPaid && (
            <Button
              variant="contained"
              startIcon={<Payments />}
              disabled={actionLoading !== null}
              onClick={() => {
                setPaymentDate(todayISO());
                setPaymentReference('');
                setMarkPaidOpen(true);
              }}
            >
              Mark Paid
            </Button>
          )}
          {canCancel && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              disabled={actionLoading !== null}
              onClick={() => setCancelOpen(true)}
            >
              Cancel Expense
            </Button>
          )}
          {!canEdit && !canSubmit && !canApproveReject && !canMarkPaid && !canCancel && (
            <Typography variant="body2" color="text.secondary">
              This expense is {exp.status.replace(/_/g, ' ').toLowerCase()} -- no further actions are available.
            </Typography>
          )}
        </Box>
      </Paper>

      {/* GL impact note */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <AccountBalance sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            GL Impact on Approval
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {glImpactLines(exp).map((line) => (
            <Typography key={line} variant="body2" sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
              {line}
            </Typography>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          The entry above posts as a balanced journal once this expense is approved. Reimbursement claims credit
          Accounts Payable instead of Bank/Cash until they are settled.
        </Typography>
        {(exp as any).journal_entry_id && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AccountBalance />}
            sx={{ mt: 1.5 }}
            component={RouterLink}
            to={`/finance/journal/${(exp as any).journal_entry_id}`}
          >
            View Accounting Entry
          </Button>
        )}
      </Paper>

      <Box sx={{ mb: 2.5 }}>
        <AttachmentsPanel entityType="expense" entityId={Number(id)} title="Receipts & Documents" />
      </Box>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Expense Details
            </Typography>
            <Grid container spacing={1.5}>
              {[
                ['Category', (exp as any).category_name || exp.category],
                ['Employee', exp.employee_name || (exp.employee_id ? String(exp.employee_id) : null)],
                ['Department', (exp as any).department_name || exp.department],
                ['Cost Center', exp.cost_center_name ? `${exp.cost_center_code} — ${exp.cost_center_name}` : null],
                ['Project', exp.project_name],
                ['Payment Method', exp.payment_method ? exp.payment_method.replace(/_/g, ' ') : null],
                ['Vendor', exp.vendor_name],
                ['GL Account', (exp as any).gl_account_name
                  ? `${(exp as any).gl_account_code_resolved || exp.gl_account_code || ''} ${(exp as any).gl_account_name}`.trim()
                  : ((exp as any).gl_account_code_resolved || exp.gl_account_code)],
                ['Approved By', exp.approved_by_name],
                ['Approved On', (exp as any).approved_at || exp.approved_date ? fmtDate((exp as any).approved_at || exp.approved_date) : null],
                ['Paid On', exp.paid_date ? fmtDate(exp.paid_date) : null],
                ['Payment Reference', exp.payment_reference],
              ].map(([label, value]) => (
                <Grid size={{ xs: 6 }} key={label}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {value || '—'}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Amounts
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid size={{ xs: 4 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Amount
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {formatIndianNumber(toNum(exp.amount))}
                </Typography>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Tax
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {formatIndianNumber(toNum(exp.tax_amount))}
                </Typography>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Total
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {formatIndianNumber(toNum(exp.total_amount))}
                </Typography>
              </Grid>
            </Grid>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Description
            </Typography>
            <Typography variant="body2" color={exp.description ? 'text.primary' : 'text.disabled'}>
              {exp.description || 'No description recorded.'}
            </Typography>
            {exp.remarks && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
                  Remarks
                </Typography>
                <Typography variant="body2">{exp.remarks}</Typography>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Approve dialog */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Approve Expense</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Approving posts this expense to the general ledger and makes it eligible for payment.
          </DialogContentText>
          <TextField
            label="Comment (optional)"
            fullWidth
            multiline
            rows={3}
            value={approveComment}
            onChange={(e) => setApproveComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApprove} disabled={actionLoading === 'approve'}>
            {actionLoading === 'approve' ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Expense</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            A reason is required and will be visible to the person who submitted this expense.
          </DialogContentText>
          <TextField
            label="Rejection Reason"
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={rejectReason.trim().length < 3 || actionLoading === 'reject'}
          >
            {actionLoading === 'reject' ? 'Rejecting...' : 'Reject Expense'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel confirm */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Expense</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This cannot be undone. {exp.status === 'APPROVED' ? 'Any GL posting made on approval will be reversed.' : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Keep It</Button>
          <Button color="error" variant="contained" onClick={handleCancel} disabled={actionLoading === 'cancel'}>
            {actionLoading === 'cancel' ? 'Cancelling...' : 'Cancel Expense'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark paid */}
      <Dialog open={markPaidOpen} onClose={() => (actionLoading ? undefined : setMarkPaidOpen(false))} maxWidth="xs" fullWidth>
        <DialogTitle>Mark Expense as Paid</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Confirms settlement of {formatIndianNumber(toNum(exp.total_amount))} via{' '}
            {exp.payment_method ? exp.payment_method.replace(/_/g, ' ') : 'the recorded payment method'}.
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Payment Date"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Payment Reference (optional)"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="UTR / cheque no. / transaction id"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidOpen(false)} disabled={actionLoading === 'mark-paid'}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleMarkPaid} disabled={actionLoading === 'mark-paid'}>
            {actionLoading === 'mark-paid' ? 'Recording...' : 'Mark Paid'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpenseDetailPage;
